import React, {createContext, useCallback, useContext, useRef, useState} from "react";
import ReactDOM from "react-dom";
import "./FCToast.scss";

export type ToastType = "success" | "error" | "info" | "warning";

interface ToastItem {
    id: number;
    type: ToastType;
    title?: string;
    message: string;
    duration: number;
    leaving: boolean;
}

interface FCToastContextValue {
    show: (type: ToastType, message: string, title?: string, duration?: number) => void;
    success: (message: string, title?: string) => void;
    error: (message: string, title?: string) => void;
    info: (message: string, title?: string) => void;
    warning: (message: string, title?: string) => void;
}

const FCToastContext = createContext<FCToastContextValue | null>(null);

const DEFAULT_DURATION = 4000;
const LEAVE_ANIMATION_MS = 220;

const ICONS: Record<ToastType, string> = {
    success: "check_circle",
    error: "error",
    info: "info",
    warning: "warning",
};

/**
 * Provider à monter en racine de l'application pour activer le système de toasts.
 */
export const FCToastProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const idRef = useRef(0);

    const dismiss = useCallback((id: number) => {
        // Marque le toast comme sortant pour jouer l'animation, puis le retire
        setToasts((prev) => prev.map((t) => t.id === id ? {...t, leaving: true} : t));
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, LEAVE_ANIMATION_MS);
    }, []);

    const show = useCallback((type: ToastType, message: string, title?: string, duration: number = DEFAULT_DURATION) => {
        idRef.current += 1;
        const id = idRef.current;
        setToasts((prev) => [...prev, {id, type, message, title, duration, leaving: false}]);
        if (duration > 0) {
            setTimeout(() => dismiss(id), duration);
        }
    }, [dismiss]);

    const value: FCToastContextValue = {
        show,
        success: (m, t) => show("success", m, t),
        error: (m, t) => show("error", m, t),
        info: (m, t) => show("info", m, t),
        warning: (m, t) => show("warning", m, t),
    };

    return (
        <FCToastContext.Provider value={value}>
            {children}
            {ReactDOM.createPortal(
                <div className="fcToastContainer" aria-live="polite" aria-atomic="true">
                    {toasts.map((t) => (
                        <div key={t.id}
                             className={`fcToast fcToast-${t.type}${t.leaving ? " fcToast-leaving" : ""}`}
                             role="status">
                            <span className="material-symbols-outlined fcToastIcon">{ICONS[t.type]}</span>
                            <div className="fcToastBody">
                                {t.title && <div className="fcToastTitle">{t.title}</div>}
                                <div className="fcToastMessage">{t.message}</div>
                            </div>
                            <button type="button"
                                    className="fcToastClose"
                                    aria-label="Fermer"
                                    onClick={() => dismiss(t.id)}>
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                    ))}
                </div>,
                document.body,
            )}
        </FCToastContext.Provider>
    );
};

/**
 * Hook d'accès au système de toasts.
 * Doit être appelé sous un `<FCToastProvider>`.
 */
export function useToast(): FCToastContextValue {
    const ctx = useContext(FCToastContext);
    if (!ctx) {
        // Fallback no-op si le provider n'est pas monté (évite les crashes)
        const noop = () => undefined;
        return {show: noop, success: noop, error: noop, info: noop, warning: noop};
    }
    return ctx;
}

export default FCToastProvider;
